import type { ChessMove, ChessView } from "@bing/engine-chess";
import type { ReactNode } from "react";
import { fa } from "../i18n/fa";
import { ChessTable } from "../screens/ChessTable";
import { useSession } from "../store/context";
import { ChessEndOverlay } from "./ChessGame";
import type { AbsentSeat, LobbyPlayer } from "./online-connection";
import { OnlineGame } from "./OnlineGame";
import type { SeatIdentity } from "./seat-identity";

/**
 * THE OTHER CHAIR, as the board names it — ONE resolver for the table and the end card (card-98
 * review E-I3 / E-M1): a human the server resolved; else, on a resumed game whose opponent has not come
 * back, NOBODY occupies it — `players` lists occupants only — so its OWNER as the server resolved them
 * (`absentSeats`, which disappears the instant anything sits down, so a real bot-fill reads «ربات»
 * again); else none — a bot-fill or an unresolved seat, the board's «ربات».
 */
function chessOpponent(
  players: readonly LobbyPlayer[] | undefined,
  absentSeats: readonly AbsentSeat[] | undefined,
  you: number,
): { identity: SeatIdentity | null; absent: boolean } {
  const opp = players?.find((p) => p.seat !== you && !p.isBot);
  const away = absentSeats?.find((a) => a.seat !== you);
  return {
    identity: opp
      ? {
          nickname: opp.name,
          avatarId: opp.user?.avatarId,
          avatarPhotoId: opp.user?.avatarPhotoId,
          isOfficial: opp.user?.isOfficial,
        }
      : away?.user
        ? {
            nickname: away.user.nickname,
            firstName: away.user.firstName,
            lastName: away.user.lastName,
            appUsername: away.user.appUsername,
            avatarId: away.user.avatarId,
            avatarPhotoId: away.user.avatarPhotoId,
            isOfficial: away.user.isOfficial,
          }
        : null,
    absent: !opp && away !== undefined,
  };
}

/** Online شطرنج — a thin wrapper over the shared `OnlineGame` shell (public quick-match
 * OR a host's private table). 2-seat, perfect-information: no CardDeck, no team overlay,
 * no bot-seat invite. Threads the self profile (own-seat photo + name) and, when the
 * opponent seat is a resolved HUMAN, their real name/avatar; a bot-fill / unresolved
 * opponent falls back to the table's default «ربات». The server clock (`clock`) is threaded
 * into the strips for a TIMED table; an untimed table sends no clock, so no chip is shown. */
export function ChessOnlineGame(): ReactNode {
  const profile = useSession((s) => s.profile);
  return (
    <OnlineGame<ChessMove, ChessView>
      gameId="chess"
      room="chess-online"
      title={fa.chess.title}
      renderTable={({
        view,
        legal,
        send,
        leave,
        players,
        clock,
        absentSeats,
        opponentAway,
        leaveIsFree,
      }) => {
        // The opponent is the OTHER seat — a resolved human, or the absent owner of an EMPTY chair
        // on a resumed game (`chessOpponent`, the end card's resolver too).
        const other = chessOpponent(players, absentSeats, view.you);
        return (
          <ChessTable
            view={view}
            legal={legal}
            onMove={send}
            onLeave={leave}
            self={profile}
            opponent={other.identity}
            clock={clock ?? null}
            opponentAbsent={other.absent}
            opponentAway={opponentAway === true}
            leaveIsFree={leaveIsFree === true}
          />
        );
      }}
      renderWin={({
        result,
        view,
        again,
        leave,
        players,
        absentSeats,
        againDisabled,
        againHint,
        againHidden,
      }) => {
        // card-98 review E-I3: the end card names the other chair exactly as the board does — an
        // absent human by name, never «ربات» (the 2026-09-04 defect)
        const other = chessOpponent(players, absentSeats, view.you);
        return (
          <ChessEndOverlay
            result={result}
            view={view}
            self={profile}
            opponent={other.identity}
            opponentAbsent={other.absent}
            onAgain={again}
            againDisabled={againDisabled}
            againHint={againHint}
            againHidden={againHidden}
            onLeave={leave}
          />
        );
      }}
    />
  );
}
